<template>
  <div>
    <!-- Hero Section (aligned with Website design language) -->
    <section ref="heroEl" class="relative overflow-hidden min-h-screen flex items-center pt-16" data-animate="hero" :class="{ 'is-visible': visible.hero }">
      <div class="absolute inset-0 bg-white dark:bg-dark transition-colors duration-300"></div>

      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto text-center">
          <div class="inline-flex items-center gap-2 rounded-full bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400 mb-10 backdrop-blur-sm transition-colors duration-300 anim-el d1">
            <span class="relative flex h-2 w-2">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span class="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
            {{ t('contact.hero.tagline') }}
          </div>

          <h1 class="text-[clamp(2.5rem,6vw,5.5rem)] font-extrabold leading-[1.05] tracking-[-0.03em] mb-8 anim-el d2">
            <span class="text-gray-900 dark:text-white transition-colors duration-300">{{ t('contact.hero.title') }}</span>
            <br />
            <span class="hero-gradient-text">{{ t('contact.hero.subtitle') }}</span>
          </h1>

          <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-12 font-medium transition-colors duration-300 anim-el d3">
            {{ t('contact.hero.description') }}
          </p>

          <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12 anim-el d4">
            <button
              type="button"
              @click="scrollToContact('form')"
              class="group inline-flex items-center h-14 px-8 bg-gray-900 dark:bg-white text-white dark:text-dark font-bold text-[15px] rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
            >
              <Mail class="mr-2.5 h-5 w-5" />
              {{ t('contact.tabs.form') }}
              <ArrowRight class="ml-2.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>

            <button
              type="button"
              @click="scrollToContact('schedule')"
              class="inline-flex items-center h-14 px-8 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium text-[15px] rounded-xl transition-all duration-300 border border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-50 dark:hover:bg-white/[0.04]"
            >
              <Calendar class="mr-2.5 h-5 w-5" />
              {{ t('contact.tabs.schedule') }}
            </button>

            <button
              type="button"
              @click="scrollToContact('quote')"
              class="inline-flex items-center h-14 px-8 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium text-[15px] rounded-xl transition-all duration-300 border border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-50 dark:hover:bg-white/[0.04]"
            >
              <Globe class="mr-2.5 h-5 w-5" />
              {{ t('contact.tabs.websiteQuote') }}
            </button>
          </div>

          <div class="flex flex-wrap justify-center gap-6 text-sm text-gray-500 dark:text-gray-400 anim-el d5">
            <div class="flex items-center gap-2">
              <div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div>
              <span>{{ t('contact.form.trustBadges.response') }}</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-6 h-6 rounded-md bg-blue-500/10 flex items-center justify-center"><Check class="h-3.5 w-3.5 text-blue-500" /></div>
              <span>{{ t('contact.form.trustBadges.clients') }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white dark:from-dark to-transparent z-10 transition-colors duration-300"></div>
    </section>

    <ContactSection />
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, onUnmounted } from 'vue'
import ContactSection from '../components/ContactSection.vue'
import { useI18n } from 'vue-i18n'
import { ArrowRight, Calendar, Check, Globe, Mail } from 'lucide-vue-next'
import { useRouter, useRoute } from 'vue-router'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const visible = reactive({})
const heroEl = ref(null)
let observer = null

const scrollToContact = async (tab) => {
  if (tab) {
    await router.replace({ path: '/contact', query: { ...route.query, tab } })
  }
  setTimeout(() => {
    const el = document.getElementById('contact-main')
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, 50)
}

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) visible[entry.target.dataset.animate] = true
    })
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })

  if (heroEl.value) observer.observe(heroEl.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

defineOptions({
  name: 'ContactPage'
})
</script> 

<style scoped>
.hero-gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Scroll-triggered entrance animations (same pattern as Website) */
.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el {
  opacity: 1;
  transform: translateY(0);
}
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.15s; }
.d3 { transition-delay: 0.25s; }
.d4 { transition-delay: 0.35s; }
.d5 { transition-delay: 0.45s; }
</style>